<script setup>
import { onMounted, computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useRoute, useRouter } from 'vue-router';
import { useTasksStore } from 'dashboard/stores/tasks';
import { format, parseISO, formatDistanceToNow, isValid } from 'date-fns';

import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Breadcrumb from 'dashboard/components-next/breadcrumb/Breadcrumb.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';
import ActivityTimeline from 'dashboard/components-next/Activities/ActivityTimeline/ActivityTimeline.vue';

const route = useRoute();
const router = useRouter();
const tasksStore = useTasksStore();
const { t } = useI18n();

const isEditing = ref(false);
const isSaving = ref(false);
const activeTab = ref('activities');
const hasError = ref(false);

const taskId = computed(() => parseInt(route.params.taskId));
const uiFlags = computed(() => tasksStore.getUIFlags);
const selectedTask = computed(() => tasksStore.getSelectedTask);

const isFetchingItem = computed(() => uiFlags.value.fetchingItem);

// Local editable copy of task data
const taskData = ref({});

const TASK_TABS_OPTIONS = [
  { key: 'ACTIVITIES', value: 'activities' },
];

const tabs = computed(() => {
  return TASK_TABS_OPTIONS.map(tab => ({
    label: t(`ACTIVITIES.TABS.${tab.key}`),
    value: tab.value,
  }));
});

const activeTabIndex = computed(() => {
  return TASK_TABS_OPTIONS.findIndex(v => v.value === activeTab.value);
});

const taskTypeOptions = [
  { value: 'call', label: t('TASKS.TYPES.CALL') },
  { value: 'email', label: t('TASKS.TYPES.EMAIL') },
  { value: 'meeting', label: t('TASKS.TYPES.MEETING') },
  { value: 'follow_up', label: t('TASKS.TYPES.FOLLOW_UP') },
  { value: 'other', label: t('TASKS.TYPES.OTHER') },
];

const priorityOptions = [
  { value: 'low', label: t('TASKS.PRIORITIES.LOW') },
  { value: 'medium', label: t('TASKS.PRIORITIES.MEDIUM') },
  { value: 'high', label: t('TASKS.PRIORITIES.HIGH') },
];

const statusOptions = [
  { value: 'pending', label: t('TASKS.STATUS.PENDING') },
  { value: 'in_progress', label: t('TASKS.STATUS.IN_PROGRESS') },
  { value: 'completed', label: t('TASKS.STATUS.COMPLETED') },
  { value: 'cancelled', label: t('TASKS.STATUS.CANCELLED') },
];

const selectedTaskTypeLabel = computed(() => {
  const type = taskTypeOptions.find(t => t.value === taskData.value?.taskType);
  return type?.label || t('TASKS.TYPES.OTHER');
});

const selectedPriorityLabel = computed(() => {
  const priority = priorityOptions.find(p => p.value === taskData.value?.priority);
  return priority?.label || t('TASKS.PRIORITIES.MEDIUM');
});

const selectedStatusLabel = computed(() => {
  const status = statusOptions.find(s => s.value === taskData.value?.status);
  return status?.label || t('TASKS.STATUS.PENDING');
});

const breadcrumbItems = computed(() => {
  const items = [
    {
      label: t('TASKS.TITLE'),
      link: '#',
    },
  ];
  if (taskData.value?.title) {
    items.push({
      label: taskData.value.title,
    });
  }
  return items;
});

const statusColorClass = computed(() => {
  if (!taskData.value?.status) return '';
  const colors = {
    pending: 'bg-n-slate-9/10 text-n-slate-11',
    in_progress: 'bg-n-blue-9/10 text-n-blue-11',
    completed: 'bg-n-teal-9/10 text-n-teal-11',
    cancelled: 'bg-n-ruby-9/10 text-n-ruby-11',
  };
  return colors[taskData.value.status] || colors.pending;
});

const priorityColorClass = computed(() => {
  if (!taskData.value?.priority) return '';
  const colors = {
    low: 'bg-n-slate-9/10 text-n-slate-11',
    medium: 'bg-n-amber-9/10 text-n-amber-11',
    high: 'bg-n-ruby-9/10 text-n-ruby-11',
  };
  return colors[taskData.value.priority] || colors.medium;
});

const taskTypeIcon = computed(() => {
  const icons = {
    call: 'i-lucide-phone',
    email: 'i-lucide-mail',
    meeting: 'i-lucide-users',
    follow_up: 'i-lucide-rotate-ccw',
    other: 'i-lucide-file-text',
  };
  return icons[taskData.value?.taskType] || icons.other;
});

const formattedDueDate = computed(() => {
  if (!taskData.value?.dueDate) return '-';
  try {
    const date = parseISO(taskData.value.dueDate);
    return format(date, 'MMM d, yyyy h:mm a');
  } catch {
    return taskData.value.dueDate;
  }
});

const formattedCompletedAt = computed(() => {
  if (!taskData.value?.completedAt) return '';
  try {
    const date = parseISO(taskData.value.completedAt);
    return format(date, 'MMM d, yyyy h:mm a');
  } catch {
    return taskData.value.completedAt;
  }
});

const isCompleted = computed(() => taskData.value?.status === 'completed');
const isCancelled = computed(() => taskData.value?.status === 'cancelled');
const canComplete = computed(() => !isCompleted.value && !isCancelled.value);

// Safely format date
const formatDate = (dateValue) => {
  if (!dateValue) return '';
  try {
    const date = typeof dateValue === 'string' ? parseISO(dateValue) : new Date(dateValue);
    if (!isValid(date)) return '';
    return formatDistanceToNow(date, { addSuffix: true });
  } catch (e) {
    return '';
  }
};

const updatedAt = computed(() => {
  return formatDate(taskData.value?.updatedAt);
});

// Initialize local data from store
const initializeData = () => {
  if (selectedTask.value && typeof selectedTask.value === 'object') {
    taskData.value = { ...selectedTask.value };
  } else {
    taskData.value = {};
  }
};

watch(() => selectedTask.value, initializeData, { immediate: true });

const goToTasksList = () => {
  if (window.history.state?.back || window.history.length > 1) {
    router.back();
  } else {
    router.push(`/app/accounts/${route.params.accountId}/crm/tasks`);
  }
};

const handleBreadcrumbClick = () => {
  goToTasksList();
};

const fetchTask = async () => {
  if (!taskId.value) return;
  hasError.value = false;
  try {
    await tasksStore.show(taskId.value);
  } catch (error) {
    hasError.value = true;
    console.error('Failed to fetch task:', error);
    useAlert(t('TASKS.FETCH_ERROR'));
  }
};

const handleUpdateTask = async () => {
  if (!taskData.value) return;

  isSaving.value = true;
  try {
    await tasksStore.update(taskId.value, {
      title: taskData.value.title,
      task_type: taskData.value.taskType,
      priority: taskData.value.priority,
      status: taskData.value.status,
      due_date: taskData.value.dueDate,
      description: taskData.value.description,
    });
    useAlert(t('TASKS.UPDATE_SUCCESS'));
    isEditing.value = false;
  } catch (error) {
    useAlert(t('TASKS.UPDATE_ERROR'));
  } finally {
    isSaving.value = false;
  }
};

const handleCompleteTask = async () => {
  try {
    await tasksStore.complete(taskId.value);
    useAlert(t('TASKS.COMPLETE_SUCCESS'));
    fetchTask();
  } catch (error) {
    useAlert(t('TASKS.COMPLETE_ERROR'));
  }
};

const handleDeleteTask = async () => {
  if (!confirm(t('TASKS.DELETE_CONFIRM'))) return;

  try {
    await tasksStore.delete(taskId.value);
    useAlert(t('TASKS.DELETE_SUCCESS'));
    goToTasksList();
  } catch (error) {
    useAlert(t('TASKS.DELETE_ERROR'));
  }
};

const handleTabChange = tab => {
  activeTab.value = tab.value;
};

const startEditing = () => {
  initializeData();
  isEditing.value = true;
};

const cancelEditing = () => {
  initializeData();
  isEditing.value = false;
};

// Refetch when taskId changes
watch(taskId, () => {
  fetchTask();
});

onMounted(() => {
  fetchTask();
});
</script>

<template>
  <div
    class="flex flex-col justify-between flex-1 h-full m-0 overflow-auto bg-n-background"
  >
    <!-- Loading State -->
    <div
      v-if="isFetchingItem"
      class="flex items-center justify-center flex-1 h-full"
    >
      <Spinner />
    </div>

    <!-- Error State -->
    <div
      v-else-if="hasError"
      class="flex flex-col items-center justify-center flex-1 h-full gap-4"
    >
      <span class="i-lucide-alert-circle text-n-ruby-9 size-12" />
      <p class="text-n-slate-11">{{ t('TASKS.FETCH_ERROR') }}</p>
      <Button :label="t('TASKS.RETRY')" @click="fetchTask" />
    </div>

    <!-- Content -->
    <section
      v-else-if="selectedTask"
      class="flex w-full h-full overflow-hidden justify-evenly bg-n-background"
    >
      <!-- Main Content -->
      <div
        class="flex flex-col w-full h-full transition-all duration-300 ltr:2xl:ml-56 rtl:2xl:mr-56"
      >
        <header class="sticky top-0 z-10 px-6 3xl:px-0">
          <div class="w-full mx-auto max-w-[40.625rem]">
            <div
              class="flex flex-col xs:flex-row items-start xs:items-center justify-between w-full py-7 gap-2"
            >
              <Breadcrumb
                :items="breadcrumbItems"
                @click="handleBreadcrumbClick"
              />
              <div class="flex items-center gap-2">
                <template v-if="!isEditing">
                  <Button
                    v-if="canComplete"
                    :label="t('TASKS.COMPLETE')"
                    size="sm"
                    teal
                    icon="i-lucide-check-circle"
                    @click="handleCompleteTask"
                  />
                  <Button
                    :label="t('TASKS.EDIT')"
                    size="sm"
                    slate
                    @click="startEditing"
                  />
                </template>
                <template v-else>
                  <Button
                    :label="t('TASKS.CANCEL')"
                    size="sm"
                    ghost
                    slate
                    @click="cancelEditing"
                  />
                  <Button
                    :label="t('TASKS.SAVE')"
                    size="sm"
                    :is-loading="isSaving"
                    @click="handleUpdateTask"
                  />
                </template>
              </div>
            </div>
          </div>
        </header>

        <main class="flex-1 px-6 overflow-y-auto 3xl:px-px">
          <div class="w-full py-4 mx-auto max-w-[40.625rem]">
            <!-- Task Header -->
            <div class="flex flex-col gap-6 pb-6">
              <!-- Title and Badges Row -->
              <div class="flex flex-col gap-4">
                <!-- Read Mode Header -->
                <div v-if="!isEditing" class="flex flex-col gap-3">
                  <div class="flex items-start justify-between gap-4">
                    <div class="flex flex-col gap-2 flex-1">
                      <h1
                        class="text-2xl font-semibold text-n-slate-12"
                        :class="{ 'line-through': isCompleted }"
                      >
                        {{ taskData.title || t('TASKS.UNTITLED') }}
                      </h1>
                      <div class="flex items-center gap-2 flex-wrap">
                        <div
                          :class="[
                            'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium',
                            statusColorClass,
                          ]"
                        >
                          <span
                            v-if="taskData.status === 'completed'"
                            class="i-lucide-check-circle size-3.5"
                          />
                          <span
                            v-else-if="taskData.status === 'in_progress'"
                            class="i-lucide-loader-2 size-3.5"
                          />
                          <span
                            v-else-if="taskData.status === 'cancelled'"
                            class="i-lucide-x-circle size-3.5"
                          />
                          <span v-else class="i-lucide-clock size-3.5" />
                          {{ selectedStatusLabel }}
                        </div>
                        <div
                          :class="[
                            'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium',
                            priorityColorClass,
                          ]"
                        >
                          <span
                            v-if="taskData.priority === 'high'"
                            class="i-lucide-arrow-up size-3.5"
                          />
                          <span
                            v-else-if="taskData.priority === 'low'"
                            class="i-lucide-arrow-down size-3.5"
                          />
                          <span v-else class="i-lucide-minus size-3.5" />
                          {{ selectedPriorityLabel }}
                        </div>
                      </div>
                    </div>
                    <!-- Task Type Icon -->
                    <div
                      class="flex items-center justify-center w-12 h-12 rounded-xl bg-n-alpha-2"
                    >
                      <span
                        :class="taskTypeIcon"
                        class="text-n-slate-11 size-6"
                      />
                    </div>
                  </div>
                </div>

                <!-- Edit Mode Header -->
                <div v-else class="flex flex-col gap-4">
                  <Input
                    v-model="taskData.title"
                    :label="t('TASKS.FORM.TITLE_LABEL')"
                    :placeholder="t('TASKS.TITLE_PLACEHOLDER')"
                  />
                  <div class="grid grid-cols-2 gap-4">
                    <div class="flex flex-col gap-2">
                      <label class="text-sm font-medium text-n-slate-12">
                        {{ t('TASKS.FORM.TYPE_LABEL') }}
                      </label>
                      <ComboBox
                        v-model="taskData.taskType"
                        :options="taskTypeOptions"
                      />
                    </div>
                    <div class="flex flex-col gap-2">
                      <label class="text-sm font-medium text-n-slate-12">
                        {{ t('TASKS.FORM.PRIORITY_LABEL') }}
                      </label>
                      <ComboBox
                        v-model="taskData.priority"
                        :options="priorityOptions"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <!-- Key Info Grid -->
              <div
                class="grid grid-cols-2 gap-4 p-4 rounded-lg bg-n-alpha-2"
              >
                <!-- Due Date -->
                <div class="flex flex-col gap-1">
                  <span class="text-xs font-medium text-n-slate-11">
                    {{ t('TASKS.FORM.DUE_DATE_LABEL') }}
                  </span>
                  <div v-if="!isEditing" class="text-sm text-n-slate-12">
                    {{ formattedDueDate }}
                  </div>
                  <Input
                    v-else
                    v-model="taskData.dueDate"
                    type="datetime-local"
                    size="sm"
                  />
                </div>

                <!-- Status (in edit mode) -->
                <div v-if="isEditing" class="flex flex-col gap-1">
                  <span class="text-xs font-medium text-n-slate-11">
                    {{ t('TASKS.FORM.STATUS_LABEL') }}
                  </span>
                  <ComboBox
                    v-model="taskData.status"
                    :options="statusOptions"
                  />
                </div>

                <!-- Task Type (in read mode) -->
                <div v-if="!isEditing" class="flex flex-col gap-1">
                  <span class="text-xs font-medium text-n-slate-11">
                    {{ t('TASKS.FORM.TYPE_LABEL') }}
                  </span>
                  <div class="text-sm text-n-slate-12">
                    {{ selectedTaskTypeLabel }}
                  </div>
                </div>
              </div>

              <!-- Relation Info Cards -->
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <!-- Related To -->
                <div
                  class="flex items-center gap-3 p-3 rounded-lg bg-n-alpha-2"
                >
                  <div
                    class="flex items-center justify-center w-10 h-10 rounded-lg bg-n-slate-3"
                  >
                    <span class="i-lucide-link text-n-slate-11 size-5" />
                  </div>
                  <div class="flex flex-col min-w-0">
                    <span class="text-xs text-n-slate-11">
                      {{ t('TASKS.FORM.RELATED_TO_LABEL') }}
                    </span>
                    <router-link
                      v-if="taskData.taskable?.id"
                      :to="`/app/accounts/${route.params.accountId}/crm/${taskData.taskableType?.toLowerCase()}s/${taskData.taskable.id}`"
                      class="text-sm font-medium text-n-slate-12 truncate hover:text-n-brand transition-colors"
                    >
                      {{ taskData.taskable.name }}
                    </router-link>
                    <span v-else class="text-sm text-n-slate-12">
                      -
                    </span>
                  </div>
                </div>

                <!-- Assigned To -->
                <div
                  class="flex items-center gap-3 p-3 rounded-lg bg-n-alpha-2"
                >
                  <Avatar
                    :src="taskData.user?.avatarUrl"
                    :name="taskData.user?.name || ''"
                    :size="40"
                    rounded-full
                  />
                  <div class="flex flex-col min-w-0">
                    <span class="text-xs text-n-slate-11">
                      {{ t('TASKS.FORM.ASSIGNEE_LABEL') }}
                    </span>
                    <span class="text-sm font-medium text-n-slate-12 truncate">
                      {{ taskData.user?.name || '-' }}
                    </span>
                  </div>
                </div>
              </div>

              <!-- Description -->
              <div class="flex flex-col gap-2">
                <span class="text-sm font-medium text-n-slate-12">
                  {{ t('TASKS.FORM.DESCRIPTION_LABEL') }}
                </span>
                <p
                  v-if="!isEditing"
                  class="text-sm text-n-slate-11 whitespace-pre-wrap"
                >
                  {{ taskData.description || '-' }}
                </p>
                <textarea
                  v-else
                  v-model="taskData.description"
                  rows="4"
                  class="w-full px-3 py-2.5 text-sm bg-n-alpha-black2 text-n-slate-12 rounded-lg outline outline-1 outline-n-weak hover:outline-n-slate-6 focus:outline-n-brand transition-all resize-none"
                  :placeholder="t('TASKS.FORM.DESCRIPTION_LABEL')"
                />
              </div>

              <!-- Completion Info (when completed) -->
              <div
                v-if="isCompleted && formattedCompletedAt"
                class="flex flex-col gap-2 p-4 rounded-lg bg-n-teal-9/10"
              >
                <div class="flex items-center gap-2">
                  <span class="i-lucide-check-circle text-n-teal-11 size-5" />
                  <span class="text-sm font-medium text-n-teal-12">
                    {{ t('TASKS.STATUS.COMPLETED') }}
                  </span>
                </div>
                <p class="text-sm text-n-teal-11">
                  {{ formattedCompletedAt }}
                </p>
                <p
                  v-if="taskData.completionNotes"
                  class="text-sm text-n-teal-12 mt-1"
                >
                  {{ taskData.completionNotes }}
                </p>
              </div>

              <!-- Last Updated -->
              <div
                v-if="updatedAt"
                class="flex items-center gap-1.5 text-xs text-n-slate-11"
              >
                <span class="i-lucide-clock size-3.5" />
                {{ t('TASKS.UPDATED_AT', { date: updatedAt }) }}
              </div>
            </div>

            <!-- Danger Zone - Delete -->
            <div
              class="flex flex-col items-start w-full gap-4 pt-6 border-t border-n-strong"
            >
              <div class="flex flex-col gap-2">
                <h6 class="text-base font-medium text-n-slate-12">
                  {{ t('TASKS.DELETE') }}
                </h6>
                <span class="text-sm text-n-slate-11">
                  {{ t('TASKS.DELETE_DESCRIPTION') }}
                </span>
              </div>
              <Button
                :label="t('TASKS.DELETE')"
                color="ruby"
                @click="handleDeleteTask"
              />
            </div>
          </div>
        </main>
      </div>

      <!-- Sidebar -->
      <div
        class="hidden lg:block overflow-y-auto justify-end min-w-52 w-full py-6 max-w-md border-l border-n-weak bg-n-solid-2"
      >
        <div class="px-6">
          <TabBar
            :tabs="tabs"
            :initial-active-tab="activeTabIndex"
            class="w-full [&>button]:w-full bg-n-alpha-black2"
            @tab-changed="handleTabChange"
          />
        </div>

        <!-- Activities Tab -->
        <div v-if="activeTab === 'activities'" class="mt-4 h-[calc(100%-3rem)]">
          <ActivityTimeline
            activitable-type="Task"
            :activitable-id="taskId"
          />
        </div>

      </div>
    </section>

    <!-- Not Found State -->
    <div
      v-else
      class="flex flex-col items-center justify-center flex-1 h-full gap-4"
    >
      <span class="i-lucide-clipboard-check text-n-slate-9 size-12" />
      <p class="text-n-slate-11">
        {{ t('TASKS.EMPTY_STATE.LIST_EMPTY_STATE_TITLE') }}
      </p>
      <Button :label="t('TASKS.BACK')" @click="goToTasksList" />
    </div>
  </div>
</template>
